/**
 * Capability levels and running counts (optum-agent Feature 87, D10), read
 * once for the plugin page's picker and every card in the grid. The counts
 * move as tasks run, so the read repeats while a page shows them.
 */
import { useQuery } from '@tanstack/react-query';

import { listCapabilityPriorities } from '@optum/api-client/endpoints/capability-priorities';
import type { CapabilityLevel, CapabilityPriority } from '@optum/api-client/types';

import { qk } from '../../lib/query-keys.js';

const REFRESH_MS = 10_000;

export interface PriorityView {
  defaultLevel: CapabilityLevel;
  /** One row per id asked for; an id the agent does not list takes the default. */
  rows: CapabilityPriority[];
}

/** The rows for `capabilityIds`, or `undefined` until the first read lands. */
export function useCapabilityPriorities(
  capabilityIds: readonly string[],
): PriorityView | undefined {
  const query = useQuery({
    queryKey: qk.capabilityPriorities(),
    queryFn: listCapabilityPriorities,
    refetchInterval: REFRESH_MS,
  });
  if (!query.data) return undefined;
  const { default_level: defaultLevel, capabilities } = query.data;
  const byId = new Map(capabilities.map((row) => [row.capability_id, row]));
  return {
    defaultLevel,
    rows: capabilityIds.map(
      (id) =>
        byId.get(id) ?? {
          capability_id: id,
          level: null,
          effective_level: defaultLevel,
          running: 0,
        },
    ),
  };
}
